type PreloadImageQueues = {
  critical: string[];
  secondary: string[];
  concurrency?: number;
  runKey?: string;
};

type NetworkInformationLike = {
  saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformationLike;
};

type IdleDeadlineLike = {
  didTimeout: boolean;
  timeRemaining: () => number;
};

type IdleCallback = (deadline: IdleDeadlineLike) => void;

type WindowWithIdleCallback = Window &
  typeof globalThis & {
    requestIdleCallback?: (callback: IdleCallback, options?: { timeout?: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

const preloadedUrls = new Set<string>();
const inFlightUrls = new Map<string, Promise<void>>();
const startedRunKeys = new Set<string>();

const DEFAULT_CONCURRENCY = 3;

const dedupe = (urls: string[]): string[] => {
  const next: string[] = [];
  const seen = new Set<string>();

  for (const url of urls) {
    if (!url || seen.has(url)) {
      continue;
    }
    seen.add(url);
    next.push(url);
  }

  return next;
};

const hasSaveDataPreference = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const connection = (navigator as NavigatorWithConnection).connection;
  return Boolean(connection?.saveData);
};

const preloadSingleImage = (url: string): Promise<void> => {
  if (preloadedUrls.has(url)) {
    return Promise.resolve();
  }

  const existing = inFlightUrls.get(url);
  if (existing) {
    return existing;
  }

  const promise = new Promise<void>((resolve) => {
    const image = new Image();

    const finish = () => {
      preloadedUrls.add(url);
      inFlightUrls.delete(url);
      resolve();
    };

    image.onload = finish;
    image.onerror = finish;
    image.src = url;
  });

  inFlightUrls.set(url, promise);
  return promise;
};

const runQueue = async (urls: string[], concurrency: number): Promise<void> => {
  if (urls.length === 0) {
    return;
  }

  const queue = [...urls];
  const workerCount = Math.min(Math.max(concurrency, 1), queue.length);

  const worker = async () => {
    while (queue.length > 0) {
      const nextUrl = queue.shift();
      if (!nextUrl) {
        continue;
      }
      await preloadSingleImage(nextUrl);
    }
  };

  await Promise.all(Array.from({ length: workerCount }, worker));
};

const scheduleIdleWork = (work: () => void): number => {
  const windowWithIdle = window as WindowWithIdleCallback;
  if (typeof windowWithIdle.requestIdleCallback === "function") {
    return windowWithIdle.requestIdleCallback(
      (deadline) => {
        if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
          work();
          return;
        }
        scheduleIdleWork(work);
      },
      { timeout: 2000 }
    );
  }

  return window.setTimeout(work, 350);
};

export const startPhasedImagePreload = ({ critical, secondary, concurrency }: PreloadImageQueues): void => {
  if (typeof window === "undefined") {
    return;
  }

  const safeConcurrency = Math.max(concurrency ?? DEFAULT_CONCURRENCY, 1);
  const criticalQueue = dedupe(critical).filter((url) => !preloadedUrls.has(url));
  const secondaryQueue = dedupe(secondary).filter(
    (url) => !preloadedUrls.has(url) && !criticalQueue.includes(url)
  );
  const shouldSkipSecondary = hasSaveDataPreference();

  void runQueue(criticalQueue, safeConcurrency).then(() => {
    if (shouldSkipSecondary || secondaryQueue.length === 0) {
      return;
    }

    scheduleIdleWork(() => {
      void runQueue(secondaryQueue, safeConcurrency);
    });
  });
};

export const startPhasedImagePreloadOnce = (queues: PreloadImageQueues): void => {
  if (queues.runKey && startedRunKeys.has(queues.runKey)) {
    return;
  }

  if (queues.runKey) {
    startedRunKeys.add(queues.runKey);
  }

  startPhasedImagePreload(queues);
};
